import { useState, KeyboardEvent } from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import styled, { css } from "styled-components";

import { Input, Label, Larger } from "@/components/Forms";
import Text from "@/components/Text";
import { AudiencableChannel } from "@/types/resources";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import { Spinner } from "@/components/Button";

const InputWrap = styled.div`
  position: relative;
`;

const StyledInput = styled(Input)`
  padding-right: 50px;

  ${({ theme, $hasError }) =>
    $hasError &&
    css`
      border-color: ${theme.colors.error} !important;
    `}
`;

const AddButton = styled(Text)`
  position: absolute;
  right: 12px;
  top: 0;
  bottom: 0;
  margin: auto 0;
`;

const StyledSpinner = styled(Spinner)`
  position: absolute;
  top: calc(50% - 9px);
  right: 0;
  margin-right: 16px;
  color: ${({ theme }) => theme.colors.purple};
`;

interface Props<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
}

const ExternalChannelField = <T extends FieldValues>({
  control,
  name,
}: Props<T>) => {
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const alerts = useAlerts();

  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
  });

  const onAdd = async () => {
    try {
      setIsLoading(true);

      const channel = await api.get<never, AudiencableChannel>(
        "/v1/testing-audiences/channel-search",
        {
          params: {
            searchableUrl: url,
          },
        }
      );

      onChange([...value, channel]);

      setUrl("");

      alerts.success("Channel successfully added", channel.name);

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setHasError(true);
      alerts.error((err as any)?.response?.data?.message);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    onAdd();
  };

  return (
    <Larger>
      <Label>External channels</Label>
      <InputWrap>
        <StyledInput
          placeholder="Add link"
          $hasError={hasError}
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setHasError(false);
          }}
          onKeyDown={onKeyDown}
        />
        {isLoading ? (
          <StyledSpinner />
        ) : (
          <AddButton
            $type="caption"
            $color="purple"
            as="button"
            type="button"
            onClick={onAdd}
          >
            Add
          </AddButton>
        )}
      </InputWrap>
    </Larger>
  );
};

export default ExternalChannelField;
